import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Users, Award, Lightbulb, ArrowRight } from 'lucide-react';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#FDFBF7] pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl tracking-tight leading-none text-[#0A1F38] mb-6">
              Creating Environments People Want to Use
            </h1>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl mx-auto">
              PEP Consulting AB combines premium design with practical functionality to create workspace and
              gaming solutions for modern environments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="pb-24 sm:pb-32" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1765371512707-9e0e96fd9e5b"
                alt="Modern premium room"
                className="rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
                Born from Real Experience
              </h2>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-6">
                PEP Consulting AB was founded by someone who spends 80+ nights per year in hotels and guest
                environments. This isn't theoretical—it's based on real frustrations with poor workspace
                setups, bad ergonomics, and missing equipment.
              </p>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-6">
                After countless hours working from uncomfortable hotel rooms, taking long Teams meetings with
                inadequate setups, and wishing for better solutions, we decided to create them ourselves.
              </p>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569]">
                Every solution we design reflects this insight: we're not just selling products—we're solving
                problems we've personally experienced.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-4">
              What Drives Us
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl mx-auto">
              Our approach combines Scandinavian design principles with practical business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Target,
                title: 'User-Focused',
                description: 'Every design decision starts with real user needs and frustrations',
              },
              {
                icon: Award,
                title: 'Premium Quality',
                description: 'No compromises on materials, aesthetics, or functionality',
              },
              {
                icon: Lightbulb,
                title: 'Practical Innovation',
                description: 'Smart solutions that actually work in real environments',
              },
              {
                icon: Users,
                title: 'Scalable Solutions',
                description: 'Designed for easy deployment and maintenance at scale',
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50"
                data-testid={`value-${index}`}
              >
                <value.icon className="text-[#C5A05B] mb-4" size={40} strokeWidth={1.5} />
                <h3 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-3">{value.title}</h3>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 sm:py-32" data-testid="about-what-we-do">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-8">
              More Than a Product Supplier
            </h2>
            <div className="space-y-6 text-left">
              <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50">
                <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-3">Concept Design</h4>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                  We create complete environment concepts tailored to your brand, space, and user needs.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50">
                <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-3">
                  Implementation Support
                </h4>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                  From procurement to installation, we handle the entire process for seamless deployment.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50">
                <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-3">Scalable Rollouts</h4>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                  Multi-location deployments with consistent quality and centralized support.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50">
                <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-3">
                  Ongoing Maintenance
                </h4>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                  Easy replenishment of accessories and components to keep everything running smoothly.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
              Let's Work Together
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-10 max-w-2xl mx-auto">
              Ready to create premium workspace and gaming environments for your spaces? Let's discuss your
              project.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-5 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-semibold text-lg transition-all duration-300 hover:bg-[#0A1F38] hover:shadow-[0_20px_40px_rgba(0,0,0,0.15)] group"
              data-testid="about-contact-cta"
            >
              Get in Touch
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;